import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { 
  Users, Mail, Gift, TrendingUp, Download, Send, Trash2, Plus,
  ChevronRight, Loader2, Search, Filter, RefreshCw, Eye, Award,
  Percent, CheckCircle, XCircle, Clock, BarChart3, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { marketingApi } from '../lib/api';

// Tier badge component
const TierBadge = ({ tier }) => {
  const config = {
    NONE: { color: 'bg-zinc-500/20 text-zinc-400', label: 'Sin tier' },
    SILVER: { color: 'bg-slate-400/20 text-slate-300', label: 'Silver (5%)' },
    GOLD: { color: 'bg-amber-500/20 text-amber-400', label: 'Gold (10%)' },
    PLATINUM: { color: 'bg-purple-500/20 text-purple-300', label: 'Platinum (15%)' },
  };
  const c = config[tier] || config.NONE;
  return <Badge className={`${c.color} text-xs`}>{c.label}</Badge>;
};

// Channel badge component
const ChannelBadge = ({ channel }) => {
  const config = {
    DIRECT: { color: 'bg-primary/20 text-primary', label: 'Directo' },
    PARKOS: { color: 'bg-blue-500/20 text-blue-400', label: 'Parkos' },
    PARKVIA: { color: 'bg-green-500/20 text-green-400', label: 'Parkvia' },
    ONEPARK: { color: 'bg-purple-500/20 text-purple-400', label: 'Onepark' },
  };
  const c = config[channel] || config.DIRECT;
  return <Badge className={`${c.color} text-xs`}>{c.label}</Badge>;
};

// Stats card
const StatsCard = ({ title, value, subtitle, icon: Icon, trend }) => (
  <div className="card-dark p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-zinc-500">{title}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
        {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
      </div>
      <div className="p-2 bg-primary/10 rounded-lg">
        <Icon className="w-5 h-5 text-primary" />
      </div>
    </div>
    {trend && (
      <div className={`mt-2 text-xs ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
        {trend > 0 ? '+' : ''}{trend}% vs mes anterior
      </div>
    )}
  </div>
);

// Skeleton for loading
const TableSkeleton = () => (
  <div className="space-y-2">
    {[...Array(5)].map((_, i) => (
      <Skeleton key={`skeleton-row-${i}`} className="h-12 w-full bg-white/5" />
    ))}
  </div>
);

const MarketingDashboard = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'es' ? es : enUS;
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [revenue, setRevenue] = useState(null);
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactSearch, setContactSearch] = useState('');
  const [contactTierFilter, setContactTierFilter] = useState('');
  const [contactChannelFilter, setContactChannelFilter] = useState('');
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    subject: '',
    html: '',
    segment: 'ALL'
  });
  const [sendingCampaign, setSendingCampaign] = useState(null);
  
  // Discount codes state
  const [discountCodes, setDiscountCodes] = useState([]);
  
  // Automation logs state
  const [automationLogs, setAutomationLogs] = useState([]);

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    try {
      const response = await marketingApi.getMetrics();
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  }, []);

  // Fetch revenue summary (avg ticket + last 30d revenue + daily avg)
  const fetchRevenue = useCallback(async () => {
    try {
      const response = await marketingApi.getRevenueSummary();
      setRevenue(response.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    }
  }, []);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    try {
      const params = {
        page: contactsPage,
        limit: 20,
        search: contactSearch || undefined,
        tier: contactTierFilter && contactTierFilter !== 'all' ? contactTierFilter : undefined,
        channel: contactChannelFilter && contactChannelFilter !== 'all' ? contactChannelFilter : undefined,
      };
      const response = await marketingApi.getContacts(params);
      setContacts(response.data.contacts);
      setContactsTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, [contactsPage, contactSearch, contactTierFilter, contactChannelFilter]);

  // Fetch campaigns
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await marketingApi.getCampaigns({ page: 1, limit: 20 });
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  }, []);

  // Fetch discount codes
  const fetchDiscountCodes = useCallback(async () => {
    try {
      const response = await marketingApi.getDiscountCodes({ page: 1, limit: 50 });
      setDiscountCodes(response.data.codes);
    } catch (error) {
      console.error('Error fetching discount codes:', error);
    }
  }, []);

  // Fetch automation logs
  const fetchAutomationLogs = useCallback(async () => {
    try {
      const response = await marketingApi.getAutomationLogs({ page: 1, limit: 50 });
      setAutomationLogs(response.data.logs);
    } catch (error) {
      console.error('Error fetching automation logs:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchMetrics(),
        fetchRevenue(),
        fetchContacts(),
        fetchCampaigns(),
        fetchDiscountCodes(),
        fetchAutomationLogs()
      ]);
      setLoading(false);
    };
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload contacts when filters change
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Export contacts
  const handleExportContacts = async () => {
    try {
      const response = await marketingApi.exportContacts({ subscribed_only: true });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      toast.success('Contactos exportados');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  // Create campaign
  const handleCreateCampaign = async () => {
    if (!newCampaign.name || !newCampaign.subject || !newCampaign.html) {
      toast.error('Completa todos los campos');
      return;
    }
    
    try {
      await marketingApi.createCampaign(newCampaign);
      toast.success('Campaña creada');
      setShowNewCampaign(false);
      setNewCampaign({ name: '', subject: '', html: '', segment: 'ALL' });
      fetchCampaigns();
    } catch (error) {
      toast.error('Error al crear campaña');
    }
  };

  // Send campaign
  const handleSendCampaign = async (campaignId) => {
    if (!confirm('¿Enviar esta campaña? Esta acción no se puede deshacer.')) return;
    
    setSendingCampaign(campaignId);
    try {
      const result = await marketingApi.sendCampaign(campaignId);
      if (result.data.success) {
        toast.success(`Campaña enviada: ${result.data.sent} emails`);
        fetchCampaigns();
      } else {
        toast.error(result.data.error);
      }
    } catch (error) {
      toast.error('Error al enviar campaña');
    } finally {
      setSendingCampaign(null);
    }
  };

  // Delete contact GDPR
  const handleDeleteContact = async (email) => {
    if (!confirm(`¿Eliminar completamente a ${email}? Esta acción es irreversible (RGPD).`)) return;
    
    try {
      await marketingApi.deleteContactGDPR(email);
      toast.success('Contacto eliminado');
      fetchContacts();
      fetchMetrics();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Segment labels
  const segmentLabels = {
    ALL: 'Todos los suscriptores',
    DIRECT_ONLY: 'Solo clientes directos',
    CHANNELS_ONLY: 'Solo canales',
    SILVER_ONLY: 'Solo Silver',
    GOLD_ONLY: 'Solo Gold',
    PLATINUM_ONLY: 'Solo Platinum',
    INACTIVE_120_DAYS: 'Inactivos +120 días'
  };

  // Automation type labels
  const automationLabels = {
    POST_CHECKOUT_COUPON: 'Cupón post-checkout',
    REVIEW_REQUEST: 'Solicitud reseña',
    BIRTHDAY_COUPON: 'Cupón cumpleaños',
    REACTIVATION_COUPON: 'Cupón reactivación',
    ABANDONED_CART: 'Carrito abandonado'
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={`loading-card-${i}`} className="h-24 bg-white/5" />
          ))}
        </div>
        <TableSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Marketing & Fidelización</h2>
          <p className="text-zinc-500">Gestiona contactos, campañas y automatizaciones</p>
        </div>
        <Button onClick={() => setShowNewCampaign(true)} className="bg-primary text-black">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Campaña
        </Button>
      </div>

      {/* Revenue summary — finalized reservations only */}
      {revenue && (
        <div
          className="rounded-2xl border border-amber-400/20 bg-gradient-to-br from-zinc-900 via-zinc-900/70 to-zinc-950 p-6"
          data-testid="marketing-revenue-summary"
        >
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="text-xs uppercase tracking-widest text-amber-400/80 font-semibold">Ingresos & Ticket Medio</p>
              <h3 className="text-lg font-bold text-white mt-1">Promedio de ingresos por reserva</h3>
              <p className="text-xs text-zinc-500 mt-1">
                Basado únicamente en reservas <span className="text-zinc-300">finalizadas</span> (confirmadas y con servicio completado).
              </p>
            </div>
            <div className="text-right">
              <span className="text-[11px] text-zinc-500">Últimos 30 días</span>
              <p className="text-xs text-zinc-400 mt-1">{revenue.count_finalized_30d} reservas</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-white/5 bg-zinc-950/70 p-5" data-testid="metric-avg-ticket">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500">Ticket medio</p>
              <p className="text-3xl font-bold text-amber-400 mt-2">
                {((revenue.avg_ticket_cents || 0) / 100).toFixed(2)} €
              </p>
              <p className="text-xs text-zinc-500 mt-2">{revenue.count_finalized_total} reservas finalizadas (total)</p>
            </div>
            <div className="rounded-xl border border-white/5 bg-zinc-950/70 p-5" data-testid="metric-revenue-30d">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500">Ingresos últimos 30 días</p>
              <p className="text-3xl font-bold text-emerald-400 mt-2">
                {((revenue.revenue_30d_cents || 0) / 100).toFixed(2)} €
              </p>
              <p className="text-xs text-zinc-500 mt-2">Servicio completado en los últimos 30 días</p>
            </div>
            <div className="rounded-xl border border-white/5 bg-zinc-950/70 p-5" data-testid="metric-avg-daily">
              <p className="text-[11px] uppercase tracking-wider text-zinc-500">Promedio diario</p>
              <p className="text-3xl font-bold text-sky-400 mt-2">
                {((revenue.avg_daily_cents_30d || 0) / 100).toFixed(2)} €
              </p>
              <p className="text-xs text-zinc-500 mt-2">Ingresos / 30 días</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard
            title="Suscriptores activos"
            value={metrics.active_subscribers}
            subtitle={`de ${metrics.total_contacts} contactos`}
            icon={UserCheck}
          />
          <StatsCard
            title="Clientes directos"
            value={`${metrics.direct_vs_channels?.direct_percent || 0}%`}
            subtitle={`${metrics.direct_vs_channels?.direct || 0} directos`}
            icon={TrendingUp}
          />
          <StatsCard
            title="Conversión cupones"
            value={`${metrics.coupon_metrics?.conversion_rate || 0}%`}
            subtitle={`${metrics.coupon_metrics?.total_used || 0} usados`}
            icon={Gift}
          />
          <StatsCard
            title="Clientes recurrentes"
            value={`${metrics.repeat_customers?.rate || 0}%`}
            subtitle={`${metrics.repeat_customers?.count || 0} repetidores`}
            icon={Award}
          />
        </div>
      )}

      {/* Tier Distribution */}
      {metrics && (
        <div className="card-dark p-4">
          <h3 className="text-white font-semibold mb-3">Distribución por Tier</h3>
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(metrics.tier_distribution || {}).map(([tier, count]) => (
              <div key={tier} className="text-center">
                <TierBadge tier={tier} />
                <p className="text-2xl font-bold text-white mt-2">{count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary">
          <TabsTrigger value="contacts" className="data-[state=active]:bg-primary data-[state=active]:text-black">
            Contactos
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-primary data-[state=active]:text-black">
            Campañas
          </TabsTrigger>
          <TabsTrigger value="codes" className="data-[state=active]:bg-primary data-[state=active]:text-black">
            Cupones
          </TabsTrigger>
          <TabsTrigger value="automation" className="data-[state=active]:bg-primary data-[state=active]:text-black">
            Automatizaciones
          </TabsTrigger>
        </TabsList>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="mt-4">
          <div className="card-dark">
            {/* Filters */}
            <div className="p-4 border-b border-white/5 flex items-center gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Buscar por email, nombre..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className="bg-secondary border-white/10"
                />
              </div>
              <Select value={contactTierFilter} onValueChange={setContactTierFilter}>
                <SelectTrigger className="w-[150px] bg-secondary border-white/10">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="NONE">Sin tier</SelectItem>
                  <SelectItem value="SILVER">Silver</SelectItem>
                  <SelectItem value="GOLD">Gold</SelectItem>
                  <SelectItem value="PLATINUM">Platinum</SelectItem>
                </SelectContent>
              </Select>
              <Select value={contactChannelFilter} onValueChange={setContactChannelFilter}>
                <SelectTrigger className="w-[150px] bg-secondary border-white/10">
                  <SelectValue placeholder="Canal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="DIRECT">Directo</SelectItem>
                  <SelectItem value="PARKOS">Parkos</SelectItem>
                  <SelectItem value="PARKVIA">Parkvia</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handleExportContacts} className="border-white/10">
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Email</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Nombre</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Canal</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Tier</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Reservas</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Suscrito</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.email} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4 text-white">{contact.email}</td>
                      <td className="p-4 text-zinc-400">{contact.name || '-'}</td>
                      <td className="p-4"><ChannelBadge channel={contact.source_channel} /></td>
                      <td className="p-4"><TierBadge tier={contact.loyalty_tier} /></td>
                      <td className="p-4 text-white">{contact.total_reservations || 0}</td>
                      <td className="p-4">
                        {contact.consent_marketing && !contact.unsubscribed ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteContact(contact.email)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="p-4 border-t border-white/5 flex items-center justify-between">
              <p className="text-sm text-zinc-500">
                Mostrando {contacts.length} de {contactsTotal} contactos
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={contactsPage === 1}
                  onClick={() => setContactsPage(p => p - 1)}
                  className="border-white/10"
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={contacts.length < 20}
                  onClick={() => setContactsPage(p => p + 1)}
                  className="border-white/10"
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="mt-4">
          <div className="card-dark">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Campaña</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Segmento</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Destinatarios</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Enviados</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Estado</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Fecha</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-zinc-500">
                        No hay campañas creadas
                      </td>
                    </tr>
                  ) : campaigns.map((campaign) => (
                    <tr key={campaign.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <p className="text-white font-medium">{campaign.name}</p>
                        <p className="text-xs text-zinc-500">{campaign.subject}</p>
                      </td>
                      <td className="p-4 text-zinc-400 text-sm">
                        {segmentLabels[campaign.segment] || campaign.segment}
                      </td>
                      <td className="p-4 text-white">{campaign.total_recipients}</td>
                      <td className="p-4">
                        <span className="text-green-400">{campaign.sent_count}</span>
                        {campaign.failed_count > 0 && (
                          <span className="text-red-400 ml-1">/ {campaign.failed_count} err</span>
                        )}
                      </td>
                      <td className="p-4">
                        <Badge className={
                          campaign.status === 'COMPLETED' ? 'bg-green-500/20 text-green-400' :
                          campaign.status === 'SENDING' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }>
                          {campaign.status}
                        </Badge>
                      </td>
                      <td className="p-4 text-zinc-400 text-sm">
                        {campaign.sent_at && !isNaN(new Date(campaign.sent_at).getTime())
                          ? format(new Date(campaign.sent_at), 'dd/MM/yyyy HH:mm', { locale })
                          : '-'}
                      </td>
                      <td className="p-4">
                        {campaign.status === 'DRAFT' && (
                          <Button
                            size="sm"
                            onClick={() => handleSendCampaign(campaign.id)}
                            disabled={sendingCampaign === campaign.id}
                            className="bg-primary text-black"
                          >
                            {sendingCampaign === campaign.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-1" />
                                Enviar
                              </>
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Discount Codes Tab */}
        <TabsContent value="codes" className="mt-4">
          <div className="card-dark">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Código</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Email</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Tipo</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Valor</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Origen</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Expira</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {discountCodes.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-zinc-500">
                        No hay cupones generados
                      </td>
                    </tr>
                  ) : discountCodes.map((code) => (
                    <tr key={code.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4 font-mono text-primary font-bold">{code.code}</td>
                      <td className="p-4 text-zinc-400 text-sm">{code.contact_email || '-'}</td>
                      <td className="p-4">
                        <Badge className={code.type === 'FIXED' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}>
                          {code.type === 'FIXED' ? 'Fijo' : 'Porcentaje'}
                        </Badge>
                      </td>
                      <td className="p-4 text-white">
                        {code.type === 'FIXED' ? `${(code.value / 100).toFixed(2)}€` : `${code.value}%`}
                      </td>
                      <td className="p-4 text-zinc-400 text-sm">{code.campaign_origin || '-'}</td>
                      <td className="p-4 text-zinc-400 text-sm">
                        {code.expires_at && !isNaN(new Date(code.expires_at).getTime())
                          ? format(new Date(code.expires_at), 'dd/MM/yyyy', { locale })
                          : '—'}
                      </td>
                      <td className="p-4">
                        {code.used ? (
                          <Badge className="bg-green-500/20 text-green-400">Usado</Badge>
                        ) : (code.expires_at && new Date(code.expires_at) < new Date()) ? (
                          <Badge className="bg-red-500/20 text-red-400">Expirado</Badge>
                        ) : (
                          <Badge className="bg-blue-500/20 text-blue-400">Activo</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Automation Logs Tab */}
        <TabsContent value="automation" className="mt-4">
          <div className="card-dark">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Fecha</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Tipo</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Email</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Cupón</th>
                    <th className="text-left p-4 text-xs text-zinc-500 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {automationLogs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-zinc-500">
                        No hay logs de automatización
                      </td>
                    </tr>
                  ) : automationLogs.map((log) => (
                    <tr key={log.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4 text-zinc-400 text-sm">
                        {log.sent_at && !isNaN(new Date(log.sent_at).getTime())
                          ? format(new Date(log.sent_at), 'dd/MM/yyyy HH:mm', { locale })
                          : '—'}
                      </td>
                      <td className="p-4">
                        <Badge className="bg-purple-500/20 text-purple-400">
                          {automationLabels[log.automation_type] || log.automation_type}
                        </Badge>
                      </td>
                      <td className="p-4 text-white">{log.contact_email}</td>
                      <td className="p-4 font-mono text-primary">{log.related_discount_code || '-'}</td>
                      <td className="p-4">
                        {log.success ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* New Campaign Dialog */}
      <Dialog open={showNewCampaign} onOpenChange={setShowNewCampaign}>
        <DialogContent className="bg-background border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Nueva Campaña de Email</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-white">Nombre de la campaña</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                placeholder="Promoción Verano 2026"
                className="bg-secondary border-white/10 mt-1"
              />
            </div>
            
            <div>
              <Label className="text-white">Asunto del email</Label>
              <Input
                value={newCampaign.subject}
                onChange={(e) => setNewCampaign({...newCampaign, subject: e.target.value})}
                placeholder="🎁 ¡Oferta especial para ti!"
                className="bg-secondary border-white/10 mt-1"
              />
            </div>
            
            <div>
              <Label className="text-white">Segmento</Label>
              <Select 
                value={newCampaign.segment} 
                onValueChange={(v) => setNewCampaign({...newCampaign, segment: v})}
              >
                <SelectTrigger className="bg-secondary border-white/10 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos los suscriptores</SelectItem>
                  <SelectItem value="DIRECT_ONLY">Solo clientes directos</SelectItem>
                  <SelectItem value="CHANNELS_ONLY">Solo canales</SelectItem>
                  <SelectItem value="SILVER_ONLY">Solo Silver</SelectItem>
                  <SelectItem value="GOLD_ONLY">Solo Gold</SelectItem>
                  <SelectItem value="PLATINUM_ONLY">Solo Platinum</SelectItem>
                  <SelectItem value="INACTIVE_120_DAYS">Inactivos +120 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-white">Contenido HTML</Label>
              <Textarea
                value={newCampaign.html}
                onChange={(e) => setNewCampaign({...newCampaign, html: e.target.value})}
                placeholder="<html><body>Tu contenido aquí...</body></html>"
                className="bg-secondary border-white/10 mt-1 min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-zinc-500 mt-1">
                El pie de página con enlaces legales y desuscripción se añadirá automáticamente.
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowNewCampaign(false)} className="border-white/10">
              Cancelar
            </Button>
            <Button onClick={handleCreateCampaign} className="bg-primary text-black">
              Crear Campaña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketingDashboard;
